/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useState } from "react";
import moment from "moment";
import { useFormContext } from "react-hook-form";
import { strings } from "../common/Strings/Strings";
import Select from "./ReactHookFormFields/General/Select/Select";
import DatePickerInput from "./ReactHookFormFields/ReactDateAndTimePickers/DatePickerInput";
import Modal from "./Modal/Modal";
import Button from "./Button";

interface Props {
  customOptionDisplayType?: "inline" | "modal";
  labelOff?: boolean;
  onChange(startDate: Date, endDate: Date): void;
}

export enum DateTemplate {
  today = "today",
  yesterday = "yesterday",
  thisWeek = "thisWeek",
  thisMonth = "thisMonth",
  previousWeek = "previousWeek",
  previousMonth = "previousMonth",
  thisYear = "thisYear",
  previousYear = "previousYear",
  pastYear = "pastYear",
  custom = "custom",
}

const DateFilters = (props: Props): ReactElement => {
  const { customOptionDisplayType = "inline", labelOff, onChange } = props;
  const [isDatesReadOnly, setDatesReadOnly] = useState<boolean>(true);
  const { control, register, setValue, getValues } = useFormContext();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const notifyOnChange = () => {
    onChange(getValues("startDate"), getValues("endDate"));
  };

  const onDateTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const dateTemplate = e.target.value as DateTemplate;
    switch (dateTemplate) {
      case DateTemplate.today:
        setValue("startDate", new Date());
        setValue("endDate", new Date());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.yesterday:
        setValue("startDate", moment().add(-1, "day").startOf("day").toDate());
        setValue("endDate", moment().add(-1, "day").endOf("day").toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.thisWeek:
        setValue("startDate", moment().startOf("week").toDate());
        setValue("endDate", moment().endOf("week").toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.previousWeek:
        setValue("startDate", moment().add(-1, "week").startOf("week").toDate());
        setValue("endDate", moment().add(-1, "week").endOf("week").toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.thisMonth:
        setValue("startDate", moment().startOf("month").toDate());
        setValue("endDate", moment().endOf("month").toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.previousMonth:
        setValue("startDate", moment().add(-1, "month").startOf("month").toDate());
        setValue("endDate", moment().add(-1, "month").endOf("month").toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.thisYear:
        setValue("startDate", moment().startOf("year").toDate());
        setValue("endDate", moment().endOf("year").toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.previousYear:
        setValue("startDate", moment().add(-1, "year").startOf("year").toDate());
        setValue("endDate", moment().add(-1, "year").endOf("year").toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;
      case DateTemplate.pastYear:
        setValue("startDate", moment().subtract(1, "year").toDate());
        setValue("endDate", moment().toDate());
        setDatesReadOnly(true);
        notifyOnChange();
        return;  
      default:
        setDatesReadOnly(false);
        setIsModalOpen(true);
    }
  };

  return (
    <div className="flex items-center space-x-4">
      <Select
        isChooseOption={false}
        label={strings.date}
        labelOff={labelOff}
        name="dateTemplate"
        onChange={onDateTemplateChange}
        options={[
          { title: strings.today, value: DateTemplate.today },
          { title: strings.yesterday, value: DateTemplate.yesterday },
          { title: strings.currentWeek, value: DateTemplate.thisWeek },
          { title: strings.previousWeek, value: DateTemplate.previousWeek },
          { title: strings.currentMonth, value: DateTemplate.thisMonth },
          { title: strings.previousMonth, value: DateTemplate.previousMonth },
          { title: strings.currentYear, value: DateTemplate.thisYear },
          { title: strings.previousYear, value: DateTemplate.previousYear },
          { title: strings.pastYear, value: DateTemplate.pastYear},
          { title: `${strings.custom}...`, value: DateTemplate.custom },
        ]}
        register={register}
      />
      {customOptionDisplayType === "inline" ? (
        <>
          {!isDatesReadOnly ? (
            <>
              <DatePickerInput
                control={control}
                label={strings.from}
                labelOff={labelOff}
                name="startDate"
                onChange={() => {
                  notifyOnChange();
                }}
                readOnly={isDatesReadOnly}
              />
              <DatePickerInput
                control={control}
                label={strings.to}
                labelOff={labelOff}
                name="endDate"
                onChange={() => {
                  notifyOnChange();
                }}
                readOnly={isDatesReadOnly}
              />
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          <Modal
            handleClose={() => {
              setIsModalOpen(false);
            }}
            show={isModalOpen}
          >
            <Modal.Header title={strings.addAddress} />
            <Modal.Body>
              <DatePickerInput
                control={control}
                label={strings.from}
                name="startDate"
                onChange={() => {
                  notifyOnChange();
                }}
              />
              <DatePickerInput
                control={control}
                label={strings.to}
                name="endDate"
                onChange={() => {
                  notifyOnChange();
                }}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                className="modal-main-button"
                onClick={() => {
                  setIsModalOpen(false);
                }}
              >
                {strings.save}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
    </div>
  );
};

export default DateFilters;
